import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, MapPin, Loader2, AlertTriangle, CheckCircle2, RefreshCcw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { mutate as offlineMutate } from "@/offline/apiClient";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const R = 6371000;
function haversine(a, b) {
  const p1 = (a.lat * Math.PI) / 180, p2 = (b.lat * Math.PI) / 180;
  const dp = ((b.lat - a.lat) * Math.PI) / 180, dl = ((b.lng - a.lng) * Math.PI) / 180;
  const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// ASM "Start Joint Call" shortcut — same geofenced Start Call as SE, but on
// success routes straight into the Joint Working activity form instead of
// the retailer dashboard, so the GPS-verified visit and the joint-working
// submission happen as one flow. See MoreMenu.jsx / FormsList.jsx for the
// normal (non-joint) forms entry point this piggybacks on.
const JOINT_FORM_CODE = "SLM-MARKET-WORK";

export default function VisitStart() {
  const { retailer_id } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const isJoint = searchParams.get("joint") === "1";
  const { hasWorkflowPerm } = useAuth();
  // Geo-fencing is a per-user workflow permission — off means this user can
  // start a call from anywhere, so skip the client-side distance gate below
  // entirely (the server skips its own gate the same way).
  const geoFenceOn = hasWorkflowPerm("GEO_FENCE");
  const [retailer, setRetailer] = useState(null);
  // Bug 4 (hotfix drop #2): visit-start no longer requires a selfie. Setting
  // is fetched for other UX (radius, hard-block) but `require_selfie` is
  // forced to false FE-side regardless of the server value.
  const [settings, setSettings] = useState({ visit_radius_m: 100, on_outside_radius: "flag", require_selfie: false });
  const [gps, setGps] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  // Bug 4: selfie state kept as unused stubs so the payload contract stays
  // stable (`selfie_ref: null` is always sent). No capture UI or gate.
  const [busy, setBusy] = useState(false);
  // Phase 29.2 — Start-Call geo-fence 428 handling.
  const [fenceBlock, setFenceBlock] = useState(null); // {distance_m, radius_m}
  const [exceptionOpen, setExceptionOpen] = useState(false);
  const [exceptionSubmitting, setExceptionSubmitting] = useState(false);
  const [exceptionReason, setExceptionReason] = useState("");
  const [exceptionStatus, setExceptionStatus] = useState(null); // pending/approved/rejected

  useEffect(() => {
    Promise.all([
      // Bug 6 interim defence: force a no-cache fresh fetch every time we
      // enter the visit-start screen. Any admin GPS update to this retailer
      // is picked up immediately (belt-and-braces with server-side canonical
      // `location` update below).
      api.get(`/retailers/${retailer_id}`, {
        headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      }).catch(async () => {
        // Fallback: search endpoint
        const { data } = await api.get("/retailers", { params: { page_size: 1 } });
        return { data: data.items?.find((r) => r.id === retailer_id) };
      }),
      api.get("/visits/settings"),
    ]).then(([{ data: r }, { data: s }]) => {
      setRetailer(r);
      // Bug 4 · always force require_selfie=false on the client side, so any
      // stale server setting can't reintroduce the visit-selfie step.
      setSettings({ ...s, require_selfie: false });
    }).catch((e) => toast.error(formatApiError(e)));
  }, [retailer_id]);

  // Looks up the Joint Working form's id by code, then routes into its
  // fill screen prefilled with visit_id/retailer_id — same query-param
  // contract FormsList.jsx / VisitInProgress.jsx already use. Falls back to
  // the normal retailer dashboard if the form isn't applicable to this user
  // (e.g. role gate) so the shortcut never dead-ends the visit.
  const goToJointForm = useCallback(async (visitId) => {
    try {
      const { data } = await api.get("/activity-forms/applicable");
      const form = (data.items || []).find((f) => f.code === JOINT_FORM_CODE);
      if (form) {
        nav(`/forms/${form.id}/fill?visit_id=${visitId}&retailer_id=${retailer_id}`,
            { replace: true });
        return;
      }
      // The Joint Working form isn't published yet (admin hasn't finished
      // configuring it) — a real, expected state, not a failure the ASM
      // caused or can act on. Fall through to the normal visit silently
      // instead of alarming them with a red error every single time.
    } catch {
      toast.error("Couldn't load Joint Working form — opening visit instead.");
    }
    nav(`/field/retailer/${retailer_id}/dashboard?visit_id=${visitId}`,
        { replace: true });
  }, [nav, retailer_id]);

  // A high-accuracy fix can legitimately take longer than 8s indoors/in a
  // shop — the first attempt timing out isn't a real failure. Auto-retry
  // once with a longer timeout and accuracy relaxed, instead of just
  // showing "Timeout expired" and making the user tap refresh themselves.
  const takeGps = useCallback(() => {
    setGpsError(null);
    if (!navigator.geolocation) return setGpsError("No GPS support");
    const attempt = (opts, isRetry) => {
      navigator.geolocation.getCurrentPosition(
        (p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: Math.round(p.coords.accuracy || 0) }),
        (e) => {
          if (!isRetry && e.code === e.TIMEOUT) {
            setGpsError("Still locating…");
            attempt({ enableHighAccuracy: false, timeout: 15000, maximumAge: 10000 }, true);
            return;
          }
          setGpsError(e.message);
        },
        opts,
      );
    };
    attempt({ enableHighAccuracy: true, timeout: 8000, maximumAge: 5000 }, false);
  }, []);

  useEffect(() => { takeGps(); }, [takeGps]);

  const distance = useMemo(() => {
    if (!gps || !retailer?.gps_lat) return null;
    return Math.round(haversine(gps, { lat: retailer.gps_lat, lng: retailer.gps_lng }));
  }, [gps, retailer]);

  const withinRadius = distance !== null && distance <= (settings.visit_radius_m || 100);
  const outletHasGps = retailer?.gps_lat != null;

  // Bug 4 (hotfix drop #2): captureSelfie removed — visit-start no longer
  // requires a selfie. Attendance punch-in remains the ONLY selfie flow.

  const start = async () => {
    if (!gps) return toast.error("Waiting for GPS…");
    // Bug 4: no selfie requirement on visit start (per-spec, only for attendance).
    setBusy(true);
    try {
      const body = {
        retailer_id, gps, selfie_ref: null,
        beat_id: retailer?.beat_id || null,
        client_ts: new Date().toISOString(),
        idempotency_key: (crypto.randomUUID?.() || `visit-start-${Date.now()}`),
      };
      const r = await offlineMutate({ method: "POST", path: "/visits/start", body,
                                      entity_type: "visits" });
      if (!r.immediate) {
        toast.info("Queued offline — visit starts once we're online");
        nav("/", { replace: true });
        return;
      }
      // Phase 29.5.1 · offlineMutate returns the server body at `.response`,
      // NOT `.data`. Consuming `r.data.id` blew up with
      // `Cannot read properties of undefined (reading 'id')` and stopped
      // navigation into the visit screen. Contract lives at
      // /app/frontend/src/offline/apiClient.js (see the mutate() return
      // shape docstring).
      const visit = r.response;
      if (!visit?.id) {
        toast.error("Visit created but response missing id — please refresh");
        return;
      }
      toast.success("Visit started");
      if (isJoint) {
        await goToJointForm(visit.id);
        return;
      }
      // Phase 29.3 — auto-open Retailer Dashboard on successful Start Call.
      // The visit remains in-progress; the dashboard is a decision-support
      // overlay. From there SEs can navigate to visit-in-progress if needed.
      nav(`/field/retailer/${retailer_id}/dashboard?visit_id=${visit.id}`,
          { replace: true });
    } catch (e) {
      // Phase 29.2 · 428 outside_geo_fence — offer exception request flow.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Drop 7 · Bug Fix B — 409 dedup: server rejects a fresh start when
      // an in_progress visit already exists at this retailer today. Resume
      // that visit instead of double-starting.
      if (status === 409 && detail?.error === "visit_already_in_progress"
          && detail?.visit_id) {
        if (isJoint) {
          toast.info("Resuming open visit");
          await goToJointForm(detail.visit_id);
          return;
        }
        // Resuming is only allowed from the outlet itself — same fence as Start Call.
        try {
          await api.post(`/visits/${detail.visit_id}/resume-check`, { gps: gps ? { lat: gps.lat, lng: gps.lng, accuracy_m: gps.accuracy_m } : null });
        } catch (re) {
          const rd = re?.response?.data?.detail;
          if (re?.response?.status === 428 && rd?.error === "outside_geo_fence") {
            toast.error(`You are ${rd.distance_m} m from this outlet. Go to the outlet (within ${rd.radius_m} m) to resume the call.`);
          } else toast.error(formatApiError(re));
          return;
        }
        toast.info("Resuming open visit");
        nav(`/field/retailer/${retailer_id}/dashboard?visit_id=${detail.visit_id}`,
            { replace: true });
        return;
      }
      if (status === 428 && detail?.error === "outside_geo_fence") {
        setFenceBlock({
          distance_m: detail.distance_m,
          radius_m: detail.radius_m,
          hard_block: !!detail.hard_block,
        });
        setExceptionStatus(null);
        toast.error(`Outside ${detail.radius_m}m start-call fence (${detail.distance_m}m away).`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setBusy(false); }
  };

  const submitException = async () => {
    if (!gps || !fenceBlock) return;
    setExceptionSubmitting(true);
    try {
      const { data } = await api.post("/visits/geo-exception-requests", {
        outlet_id: retailer_id,
        distance_m: fenceBlock.distance_m,
        gps,
        reason: exceptionReason || null,
      });
      setExceptionStatus(data.status || "pending");
      setExceptionOpen(false);
      toast.success("Exception request sent to manager");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExceptionSubmitting(false);
    }
  };

  const blocked = geoFenceOn && settings.on_outside_radius === "block" && distance != null && !withinRadius;

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="visit-start-screen">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500 flex items-center gap-1" data-testid="visit-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">
          {isJoint ? "Start joint call" : "Start visit"}
        </div>
        <h1 className="text-xl font-extrabold text-neutral-900">{retailer?.name || "…"}</h1>
        {isJoint && (
          <div className="text-xs text-emerald-700 font-medium mt-0.5">
            GPS check-in + Joint Working form, in one step
          </div>
        )}
        <div className="text-xs text-neutral-500 mt-0.5">
          {retailer?.code} · {retailer?.address || "no address"}
        </div>
      </div>

      {/* GPS card */}
      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900">
              <MapPin className="h-4 w-4 text-[#E5197F]" /> Live GPS
            </div>
            <Button size="sm" variant="ghost" onClick={takeGps} data-testid="visit-refresh-gps">
              <RefreshCcw className="h-3.5 w-3.5" />
            </Button>
          </div>
          {gps ? (
            <div className="text-xs font-mono text-neutral-600">
              {gps.lat.toFixed(6)}, {gps.lng.toFixed(6)} · ±{gps.accuracy_m}m
            </div>
          ) : (
            <div className="text-xs text-neutral-400 italic">{gpsError || "Locating…"}</div>
          )}
          {distance != null && (
            <div className={`text-sm font-semibold ${withinRadius || !geoFenceOn ? "text-green-700" : "text-amber-700"}`}
                 data-testid="visit-distance-badge">
              {withinRadius ? (
                <><CheckCircle2 className="h-4 w-4 inline mr-1" />{distance}m from outlet — in radius</>
              ) : !geoFenceOn ? (
                <><CheckCircle2 className="h-4 w-4 inline mr-1" />{distance}m from outlet — geo-fencing off</>
              ) : (
                <><AlertTriangle className="h-4 w-4 inline mr-1" />{distance}m from outlet — outside {settings.visit_radius_m}m radius</>
              )}
            </div>
          )}
          {!outletHasGps && (
            <div className="text-xs px-3 py-2 rounded-lg bg-[#FFD93D]/30 text-neutral-800">
              📍 This outlet has no GPS yet. Your check-in will capture the GPS for manager approval.
            </div>
          )}
          {blocked && (
            <div className="text-xs px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 font-medium"
                 data-testid="visit-blocked-msg">
              Move within {settings.visit_radius_m}m of the outlet to check in.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bug 4 (hotfix drop #2): selfie card removed — visit-start does not
          require a selfie. Attendance punch-in remains the sole selfie flow. */}

      {/* Phase 29.2 · Start-call fence block banner + exception CTA */}
      {fenceBlock && exceptionStatus !== "approved" && (
        <Card className="border-red-200 rounded-2xl bg-red-50">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-red-800">
              <ShieldAlert className="h-4 w-4" /> Start Call blocked
            </div>
            <div className="text-xs text-red-700">
              You are <b>{fenceBlock.distance_m}m</b> away — the start-call fence is{" "}
              <b>{fenceBlock.radius_m}m</b>. Move closer, or request a manager exception.
            </div>
            {exceptionStatus === "pending" && (
              <div className="text-xs text-amber-800 bg-amber-100 rounded-lg px-2 py-1"
                   data-testid="visit-fence-pending-badge">
                Exception request pending manager approval. Retry after they approve.
              </div>
            )}
            {exceptionStatus === "rejected" && (
              <div className="text-xs text-red-800 bg-red-100 rounded-lg px-2 py-1"
                   data-testid="visit-fence-rejected-badge">
                Exception rejected. Move within fence to check in.
              </div>
            )}
            {(exceptionStatus === null || exceptionStatus === "rejected") && (
              <Button onClick={() => setExceptionOpen(true)}
                      className="w-full h-11 rounded-xl bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="visit-fence-request-btn">
                Request manager exception
              </Button>
            )}
            {exceptionStatus === "pending" && (
              <Button onClick={start}
                      variant="outline"
                      className="w-full h-11 rounded-xl"
                      data-testid="visit-fence-retry-btn">
                <RefreshCcw className="h-4 w-4 mr-2" />Retry Start Call
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={exceptionOpen} onOpenChange={setExceptionOpen}>
        <DialogContent className="max-w-sm" data-testid="visit-fence-dialog">
          <DialogHeader>
            <DialogTitle>Request geo-exception</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-neutral-600 mb-2">
            Tell your manager why you need to check in from{" "}
            <b>{fenceBlock?.distance_m}m</b> away.
          </div>
          <Textarea rows={3} value={exceptionReason}
                    onChange={(e) => setExceptionReason(e.target.value)}
                    placeholder="e.g. Traffic diversion, outlet moved, GPS drift"
                    data-testid="visit-fence-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setExceptionOpen(false)}
                    data-testid="visit-fence-cancel">Cancel</Button>
            <Button onClick={submitException} disabled={exceptionSubmitting}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="visit-fence-submit">
              {exceptionSubmitting
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start button */}
      <Button onClick={start} disabled={busy || blocked || !gps}
              className="w-full h-14 tulsi-gradient text-white text-base font-bold rounded-2xl"
              data-testid="visit-start-btn">
        {busy ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : null}
        {isJoint ? "Start joint call" : "Start visit"}
      </Button>
    </div>
  );
}
